import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, ChevronRight, MapPin, Users, X } from 'lucide-react'
import { API_CONFIG } from '../config/settings'
import DateRangePickerNew from './roomDetails/booking/DateRangePickerNew'
import LocationModal, { LocationOption } from './shared/LocationModal'
import { DEFAULT_SEARCH_RADIUS_KM } from '../lib/googleMaps'
import GuestsModal, { GuestCounts } from './shared/GuestsModal'
import TravelGuide from './TravelGuide'
import ManaunaSplashSection from './home/ManaunaSplashSection'
import {
  CityCatalogItem,
  fetchCityCatalog,
  fetchSearchDefaults,
  localDefaultDates,
  FALLBACK_CITY,
} from '../services/searchDefaults'

const isManaunaCity = (city?: string) => {
  const c = (city || '').toLowerCase()
  return c.includes('manauna') || c.includes('manona') || c.includes('aonla')
}

const Home = () => {
  const navigate = useNavigate()
  const [cities, setCities] = useState<CityCatalogItem[]>([])
  const [selectedCity, setSelectedCity] = useState<CityCatalogItem | null>(null)
  const [searchParams, setSearchParams] = useState({
    location: '',
    latitude: '' as string | number,
    longitude: '' as string | number,
    checkIn: '',
    checkOut: '',
    guests: '2'
  })
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [showGuestsModal, setShowGuestsModal] = useState(false)
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [dateErrors, setDateErrors] = useState<{ [key: string]: string }>({})

  const [guestCounts, setGuestCounts] = useState<GuestCounts>({
    adults: 2,
    children: 0,
    infants: 0,
    pets: 0
  })
  const [popularLocations, setPopularLocations] = useState<LocationOption[]>([])

  const calculateNights = () => {
    if (searchParams.checkIn && searchParams.checkOut) {
      const diffTime = Math.abs(
        new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()
      )
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }
    return 0
  }
  const nights = calculateNights()

  useEffect(() => {
    const loadCities = async () => {
      try {
        const list = await fetchCityCatalog()
        setCities(list)
        const manauna = list.find((c) => c.id === 'manauna' || isManaunaCity(c.city)) || list[0]
        if (manauna) {
          setSelectedCity(manauna)
          setSearchParams((prev) => ({ ...prev, location: manauna.location }))
          setPopularLocations(
            list.map((c) => ({
              name: c.location,
              description: `${c.room_count} rooms listed`,
            }))
          )
        }
      } catch {
        setCities([FALLBACK_CITY])
        setSelectedCity(FALLBACK_CITY)
        setSearchParams((prev) => ({ ...prev, location: FALLBACK_CITY.location }))
      }
    }
    loadCities()
  }, [])

  useEffect(() => {
    if (!selectedCity) return
    let cancelled = false
    const applyDefaults = async () => {
      try {
        const defaults = await fetchSearchDefaults(selectedCity.city, 5)
        if (cancelled) return
        setSearchParams((prev) => ({
          ...prev,
          location: selectedCity.location,
          checkIn: defaults.check_in,
          checkOut: defaults.check_out,
          guests: String(defaults.guests_default || 2),
        }))
        setGuestCounts((prev) => ({ ...prev, adults: defaults.guests_default || 2 }))
      } catch {
        if (cancelled) return
        const fb = localDefaultDates(5)
        setSearchParams((prev) => ({
          ...prev,
          location: selectedCity.location,
          checkIn: fb.checkIn,
          checkOut: fb.checkOut,
          guests: '2',
        }))
      }
    }
    applyDefaults()
    return () => {
      cancelled = true
    }
  }, [selectedCity?.city, selectedCity?.location])

  const selectCity = (city: CityCatalogItem) => {
    setSelectedCity(city)
    setSearchParams((prev) => ({
      ...prev,
      location: city.location,
      latitude: '',
      longitude: '',
    }))
  }

  const hero = selectedCity?.hero
  const heroBg = isManaunaCity(selectedCity?.city)
    ? `${import.meta.env.BASE_URL}manauna-dham/night.webp`
    : `${import.meta.env.BASE_URL}manauna-dham/Gathering.webp`

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LOCATIONS}`)
        const data = await response.json()
        if (data.success && data.locations && cities.length === 0) {
          setPopularLocations(
            data.locations.map((loc: any) => ({
              name: loc.location || `${loc.city}, ${loc.state}`,
              description: loc.country || 'Available location'
            }))
          )
        }
      } catch {
        if (cities.length === 0) setPopularLocations([])
      }
    }
    fetchLocations()
  }, [cities.length])

  const services = [
    {
      id: 'home-stays',
      title: 'Home Stays',
      description: 'Experience local hospitality',
      image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80'
    },
    {
      id: 'service-apartments',
      title: 'Service Apartments',
      description: 'Comfortable extended stays',
      image: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800&q=80'
    },
    {
      id: 'rooms',
      title: 'Rooms',
      description: 'Cozy rooms for your stay',
      image: 'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&q=80'
    },
    {
      id: 'car-rentals',
      title: 'Car Rentals',
      description: 'Rent a car for your journey',
      image: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?w=800&q=80'
    },
    {
      id: 'cabs',
      title: 'Cabs',
      description: 'Book a cab for travel',
      image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=800&q=80'
    },
    {
      id: 'marriage-hall',
      title: 'Marriage Hall Booking',
      description: 'Book venues for weddings',
      image: 'https://images.unsplash.com/photo-1519167758481-83f550bb49b3?w=800&q=80'
    },
    {
      id: 'party-events',
      title: 'Party Events',
      description: 'Venues for celebrations',
      image: 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=800&q=80'
    }
  ]

  const handleSearch = () => {
    const params = new URLSearchParams()
    if (searchParams.location) params.append('location', searchParams.location)
    if (searchParams.latitude !== '' && searchParams.longitude !== '') {
      params.append('lat', String(searchParams.latitude))
      params.append('lng', String(searchParams.longitude))
      params.append('radius_km', String(DEFAULT_SEARCH_RADIUS_KM))
    }
    if (searchParams.checkIn) params.append('checkin', searchParams.checkIn)
    if (searchParams.checkOut) params.append('checkout', searchParams.checkOut)
    if (searchParams.guests) params.append('guests', searchParams.guests)
    setMobileSearchOpen(false)
    navigate(`/rooms?${params.toString()}`)
  }

  const totalGuests = guestCounts.adults + guestCounts.children + guestCounts.infants
  const getGuestText = () => {
    const parts: string[] = []
    if (guestCounts.adults > 0)
      parts.push(`${guestCounts.adults} ${guestCounts.adults === 1 ? 'adult' : 'adults'}`)
    if (guestCounts.children > 0)
      parts.push(`${guestCounts.children} ${guestCounts.children === 1 ? 'child' : 'children'}`)
    if (guestCounts.infants > 0)
      parts.push(`${guestCounts.infants} ${guestCounts.infants === 1 ? 'infant' : 'infants'}`)
    if (guestCounts.pets > 0)
      parts.push(`${guestCounts.pets} ${guestCounts.pets === 1 ? 'pet' : 'pets'}`)
    return parts.length === 0 ? '2 adults' : parts.join(', ')
  }
  const guestText = getGuestText()

  useEffect(() => {
    setSearchParams((prev) => ({ ...prev, guests: totalGuests.toString() }))
  }, [totalGuests])

  useEffect(() => {
    if (mobileSearchOpen) document.body.style.overflow = 'hidden'
    else document.body.style.overflow = ''
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileSearchOpen])

  const getDateDisplay = () => {
    if (searchParams.checkIn && searchParams.checkOut) {
      return `${new Date(searchParams.checkIn).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })} – ${new Date(searchParams.checkOut).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })}`
    }
    return ''
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ═══════════════════════════════════════════════════════
          HERO + SEARCH
          ═══════════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-secondary-800 via-secondary-900 to-secondary-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-900/90 via-secondary-900/85 to-secondary-950/95" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary-500/8 blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-80 h-80 rounded-full bg-primary-500/10 blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-8 md:pt-10 md:pb-12 lg:pt-12 lg:pb-14">
          <div className="text-center mb-6 md:mb-8">
            <p className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-primary-400 font-bold mb-2">
              Aonla Stay · Pilgrimage hospitality
            </p>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              Find stays near{' '}
              <span className="manauna-shimmer-text">{hero?.headline || 'your destination'}</span>
            </h1>
            <p className="mt-2 md:mt-3 text-white/60 text-sm sm:text-base max-w-2xl mx-auto">
              {hero?.subtitle || 'Homestays, BnBs, hotels & yatra services—book before the rush'}
            </p>

            {/* City switch — Manauna priority, other cities from DB */}
            {cities.length > 0 && (
              <div className="mt-5 flex justify-center">
                <div className="inline-flex max-w-full overflow-x-auto gap-2 p-1.5 rounded-2xl bg-white/10 backdrop-blur-md border border-white/15 scrollbar-hide">
                  {cities.map((city) => {
                    const active = selectedCity?.city === city.city && selectedCity?.state === city.state
                    return (
                      <button
                        key={`${city.city}-${city.state}`}
                        type="button"
                        onClick={() => selectCity(city)}
                        className={`shrink-0 px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                          active
                            ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        {isManaunaCity(city.city) ? 'Manauna Dham' : city.city}
                        {city.room_count > 0 && (
                          <span className={`ml-1.5 text-[10px] font-medium ${active ? 'text-white/90' : 'text-white/50'}`}>
                            {city.room_count}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── MOBILE search trigger ── */}
          <div className="md:hidden">
            <button
              onClick={() => setMobileSearchOpen(true)}
              className="w-full flex items-center gap-3 bg-white rounded-2xl p-4 shadow-xl active:scale-[0.98] transition-transform"
            >
              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center shrink-0">
                <Search className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-semibold text-gray-900 truncate">
                  {searchParams.location || 'Where are you going?'}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {getDateDisplay() || 'Any dates'} &middot; {guestText}
                </div>
              </div>
            </button>
          </div>

          {/* ── DESKTOP search bar (Airbnb-style pill, all triggers open modals) ── */}
          <div className="hidden md:block relative">
            <div className="flex items-center gap-1 bg-white rounded-full shadow-2xl border border-white/20 p-2 hover:shadow-[0_25px_50px_rgba(0,0,0,0.3)] transition-shadow max-w-4xl mx-auto">
              {/* Location → modal */}
              <button
                type="button"
                onClick={() => setShowLocationModal(true)}
                className="flex-1 min-w-0 px-5 py-3 text-left cursor-pointer hover:bg-gray-50 rounded-full transition-colors"
              >
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                  Location
                </div>
                <div className="text-sm text-gray-900 truncate">
                  {searchParams.location || (
                    <span className="text-gray-400">Where are you going?</span>
                  )}
                </div>
              </button>

              <div className="w-px h-8 bg-gray-200 shrink-0" />

              {/* Dates → modal (DateRangePickerNew) */}
              <button
                type="button"
                onClick={() => setShowDatePicker(true)}
                className="flex-1 min-w-0 px-5 py-3 text-left cursor-pointer hover:bg-gray-50 rounded-full transition-colors"
              >
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                  Check-in & Check-out
                </div>
                <div className="text-sm text-gray-900">
                  {getDateDisplay() || <span className="text-gray-400">Select dates</span>}
                </div>
              </button>

              <div className="w-px h-8 bg-gray-200 shrink-0" />

              {/* Guests → modal */}
              <button
                type="button"
                onClick={() => setShowGuestsModal(true)}
                className="flex-1 min-w-0 px-5 py-3 text-left cursor-pointer hover:bg-gray-50 rounded-full transition-colors"
              >
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-0.5">
                  Guests
                </div>
                <div className="text-sm text-gray-900 truncate">{guestText}</div>
              </button>

              <button
                onClick={handleSearch}
                className="bg-primary-500 hover:bg-primary-600 text-white rounded-full p-3.5 transition-colors active:scale-95 shrink-0 shadow-lg shadow-primary-500/30"
                aria-label="Search"
              >
                <Search className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════
          MOBILE FULL-SCREEN SEARCH (consolidated triggers → modals)
          ═══════════════════════════════════════════════════════ */}
      {mobileSearchOpen && (
        <div className="fixed inset-0 z-[70] bg-white md:hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-white sticky top-0">
            <button
              onClick={() => setMobileSearchOpen(false)}
              className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center active:bg-gray-200"
            >
              <X className="h-5 w-5 text-gray-700" />
            </button>
            <div className="text-sm font-semibold text-gray-900">Plan your stay</div>
            <div className="w-9" />
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-3">
            <button
              onClick={() => setShowLocationModal(true)}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-gray-200 text-left hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Location
                </div>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {searchParams.location || 'Where are you going?'}
                </div>
              </div>
            </button>

            <button
              onClick={() => setShowDatePicker(true)}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-gray-200 text-left hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                <Search className="h-5 w-5 text-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Check-in & Check-out
                </div>
                <div className="text-sm font-medium text-gray-900 truncate">
                  {getDateDisplay() || 'Select dates'}
                </div>
              </div>
            </button>

            <button
              onClick={() => setShowGuestsModal(true)}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-gray-200 text-left hover:border-primary-300 hover:bg-primary-50/30 transition-colors"
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                <Users className="h-5 w-5 text-primary-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  Guests
                </div>
                <div className="text-sm font-medium text-gray-900 truncate">{guestText}</div>
              </div>
            </button>
          </div>

          <div className="sticky bottom-0 border-t bg-white px-4 py-3 flex items-center gap-3 safe-area-bottom">
            <button
              onClick={() => {
                setSearchParams({
                  location: selectedCity?.location || '',
                  latitude: '',
                  longitude: '',
                  checkIn: '',
                  checkOut: '',
                  guests: '2'
                })
                setGuestCounts({ adults: 2, children: 0, infants: 0, pets: 0 })
              }}
              className="text-sm font-semibold text-gray-600 underline underline-offset-2"
            >
              Clear all
            </button>
            <button
              onClick={handleSearch}
              className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98] transition-transform shadow-lg shadow-primary-500/25"
            >
              <Search className="h-4 w-4" />
              Search
            </button>
          </div>
        </div>
      )}

      {/* Shared modals (Material card style) */}
      <LocationModal
        isOpen={showLocationModal}
        value={searchParams.location}
        prefetched={popularLocations}
        onClose={() => setShowLocationModal(false)}
        onSelect={(selected) =>
          setSearchParams((prev) => ({
            ...prev,
            location: selected.label,
            latitude: selected.latitude ?? '',
            longitude: selected.longitude ?? '',
          }))
        }
      />
      <DateRangePickerNew
        checkIn={searchParams.checkIn}
        checkOut={searchParams.checkOut}
        nights={nights}
        errors={dateErrors}
        onChange={(name, value) => {
          setSearchParams((prev) => ({ ...prev, [name]: value }))
          setDateErrors({})
        }}
        showHeading={false}
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
      />
      <GuestsModal
        isOpen={showGuestsModal}
        counts={guestCounts}
        onChange={setGuestCounts}
        onClose={() => setShowGuestsModal(false)}
      />

      <ManaunaSplashSection city={selectedCity} />

      {/* ═══════════════════════════════════════════════════════
          SERVICES SECTION — yatra-focused
          ═══════════════════════════════════════════════════════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12 md:py-16 bg-gradient-to-b from-white to-secondary-50/80">
        <div className="mb-6 sm:mb-8">
          <p className="text-[10px] uppercase tracking-[0.2em] text-primary-600 font-bold mb-1.5">Beyond the Dham</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1.5">More for your journey</h2>
          <p className="text-sm sm:text-base text-gray-500">Transport, events, and extended stays across Aonla & Bareilly</p>
        </div>

        <div className="sm:hidden -mx-4 px-4">
          <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4">
            {services.map((service) => (
              <div
                key={service.id}
                onClick={() => navigate(`/services/${service.id}`)}
                className="snap-start shrink-0 w-[72vw] max-w-[280px] group relative rounded-2xl overflow-hidden cursor-pointer active:scale-[0.97] transition-transform"
              >
                <div className="relative h-52">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      ;(e.target as HTMLImageElement).src = `https://via.placeholder.com/800x600?text=${encodeURIComponent(service.title)}`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-4">
                    <h3 className="text-base font-bold text-white">{service.title}</h3>
                    <p className="text-xs text-white/75 mt-0.5">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {services.map((service) => (
            <div
              key={service.id}
              onClick={() => navigate(`/services/${service.id}`)}
              className="group relative rounded-2xl overflow-hidden cursor-pointer transform transition-all duration-300 hover:scale-[1.03] hover:shadow-2xl"
            >
              <div className="relative h-56 md:h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  onError={(e) => {
                    ;(e.target as HTMLImageElement).src = `https://via.placeholder.com/800x600?text=${encodeURIComponent(service.title)}`
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-500/50 to-primary-600/30 group-hover:from-primary-500/60 group-hover:to-primary-600/40 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 bg-white/20 backdrop-blur-lg border-t border-white/30 p-5 rounded-b-2xl">
                  <h3 className="text-lg font-bold text-white mb-1">{service.title}</h3>
                  <p className="text-sm text-white/90">{service.description}</p>
                </div>
                <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-white/30 backdrop-blur-sm rounded-full p-2">
                    <ChevronRight className="h-5 w-5 text-white" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <TravelGuide />
    </div>
  )
}

export default Home
